/**
 * Web demo entry point.
 *
 * This is the entry point for the browser-based demo version of SQL Studio.
 * It installs the electronAPI shim before rendering the app, ensuring that
 * all Electron-dependent code has a working fallback.
 */

// Install the electronAPI shim BEFORE importing any other modules
// This ensures window.electronAPI is available when stores initialize
import './electronApiShim';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from '../App';
import '../index.css';
import '../features/browser-mode/browserMode.css';
import { BrowserModeShell, isEmbedMode } from '../features/browser-mode';
import { setSharedQueryClient } from '../core/query/sharedQueryClient';
import { initDarkMode } from '../utils/darkMode';
import { installKeybindingDispatcher } from '../features/keybindings/dispatch/installKeybindingDispatcher';

// Initialize dark mode
initDarkMode();

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Parity with `main.tsx`: the non-React readers and writers of the shared database
// listing (`invalidateDatabaseListing`, `readDatabaseListingThroughCache`,
// `updateConnection`'s removal of a stale entry) reach the cache only through this
// registration. Without it they all no-op in the browser build while
// `useDatabaseList` still caches through the provider's client with
// `gcTime: Infinity` — one half of the contract without the other.
setSharedQueryClient(queryClient);

// Parity with `main.tsx`: every app keyboard shortcut (Ctrl+B sidebar, Ctrl/Cmd+
// Enter and F5 execute, Shift+Enter, Format SQL, …) is dispatched by this one
// window-capture listener since the central keybinding registry (1.5.1) removed
// the per-component handlers. Without it the browser build had no shortcuts.
installKeybindingDispatcher();

// Check if we're in embed mode (for homepage interactive demo)
// In embed mode, skip the BrowserModeShell and show the app directly
const embedMode = isEmbedMode();

// Embed mode renders the UI at 80% (see `html.embed-mode body` in browserMode.css)
// so the fixed-height homepage iframe fits more of the app without looking cramped.
if (embedMode) {
  document.documentElement.classList.add('embed-mode');
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {embedMode ? (
        <div className="embed-mode-container">
          <App />
        </div>
      ) : (
        <BrowserModeShell>
          <App />
        </BrowserModeShell>
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);
